import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/app/common";
import { inr } from "@/lib/format";

const COMPS = [["freight", "Freight"], ["loading", "Loading"], ["unloading", "Unloading"]];

// LM budget pro-rated by the qty share of each LM on this movement (same rule the server applies on save)
export function budgetFor(plans) {
  const byLm = {};
  plans.forEach((p) => {
    const g = byLm[p.lm_id] || (byLm[p.lm_id] = { ctx: p.lm_context || {}, qty: 0, no: p.lm_number });
    g.qty += p.lines.reduce((s, l) => s + (l.balance_qty || 0), 0);
  });
  const groups = Object.values(byLm).map((g) => ({ ...g, share: g.ctx.qty_total ? Math.min(g.qty / g.ctx.qty_total, 1) : 1 }));
  const bud = Object.fromEntries(COMPS.map(([c]) => [c, groups.reduce((s, g) => s + (g.ctx[`${c}_budget`] || 0) * g.share, 0)]));
  return { bud, groups };
}

export function BudgetCompare({ plans, est, setEst }) {
  const { bud, groups } = budgetFor(plans);
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const totB = COMPS.reduce((s, [c]) => s + bud[c], 0);
  const totE = COMPS.reduce((s, [c]) => s + (num(est[c]) || 0), 0);
  const over = totB > 0 && (COMPS.some(([c]) => num(est[c]) != null && num(est[c]) > bud[c] + 1e-9) || totE > totB + 1e-9);
  const status = !totB ? "no_budget" : COMPS.every(([c]) => num(est[c]) == null) ? "not_estimated" : over ? "over_budget" : "within_budget";
  const ctxs = groups.filter((g) => g.ctx.preferred_transport_mode || g.ctx.dispatch_instructions);
  return (
    <div className="rounded border border-slate-200 p-3 text-xs space-y-2" data-testid="mfp-budget-compare">
      <div className="flex items-center gap-2"><b className="text-sm">Logistics estimate vs LM budget</b><StatusBadge status={status} testid="mfp-budget-status" /></div>
      {ctxs.map((g) => <div key={g.no} className="text-slate-600"><span className="font-mono">{g.no}</span>: {g.ctx.preferred_transport_mode && <>preferred <b>{g.ctx.preferred_transport_mode}</b>. </>}{g.ctx.dispatch_instructions}</div>)}
      <div className="overflow-x-auto"><table className="w-full">
        <thead><tr className="text-left text-slate-500"><th className="py-1">Component</th><th className="text-right">Budget share</th><th className="pl-3">Estimate (₹)</th><th className="text-right">Variance</th><th /></tr></thead>
        <tbody>
          {COMPS.map(([c, l]) => {
            const e = num(est[c]);
            const bad = totB > 0 && e != null && e > bud[c] + 1e-9;
            return (
              <tr key={c} className="border-t border-slate-100">
                <td className="py-1">{l}</td><td className="text-right font-mono">{inr(bud[c])}</td>
                <td className="pl-3"><Input type="number" min="0" className="h-8 w-28" value={est[c] ?? ""} onChange={(ev) => setEst({ ...est, [c]: ev.target.value })} data-testid={`mfp-est-${c}`} /></td>
                <td className={`text-right font-mono ${bad ? "text-red-600 font-semibold" : ""}`}>{e == null ? "—" : inr(e - bud[c])}</td>
                <td className="pl-2">{bad && <span className="text-red-600 font-semibold" data-testid={`mfp-over-${c}`}>Over</span>}</td>
              </tr>
            );
          })}
          <tr className="border-t border-slate-300 font-semibold"><td className="py-1">Total</td><td className="text-right font-mono">{inr(totB)}</td><td className="font-mono pl-3">{inr(totE)}</td><td className={`text-right font-mono ${over ? "text-red-600" : ""}`} data-testid="mfp-total-variance">{inr(totE - totB)}</td><td /></tr>
        </tbody>
      </table></div>
      <p className="text-[11px] text-slate-500">Budget share = LM logistics budget × this movement's share of the LM quantity ({groups.map((g) => `${g.no} ${Math.round(g.share * 100)}%`).join(", ")}).</p>
    </div>
  );
}
